import * as pdfjsLib from "pdfjs-dist"

// IMPORTANT:
// Do NOT statically import worker in prod bundles.
// Instead resolve dynamically so it works in Vite/Webpack/Next.
let workerInitialized = false

function ensureWorker() {
  if (workerInitialized) return

  try {
    pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
      "pdfjs-dist/build/pdf.worker.mjs",
      import.meta.url
    ).toString()

    workerInitialized = true
  } catch (err) {
    console.error("[pdf] Worker init failed", err)
  }
}

const DEFAULT_SCALE = 2

/**
 * Converts first page of PDF blob → PNG dataURL
 * Safe for React + concurrent calls
 */
export async function pdfBlobToImageDataUrl(
  pdfBlob: Blob,
  options?: {
    scale?: number
    page?: number
    signal?: AbortSignal
  }
): Promise<string | undefined> {
  if (!pdfBlob) return

  const scale = options?.scale ?? DEFAULT_SCALE
  const pageNumber = options?.page ?? 1
  const signal = options?.signal

  ensureWorker()

  let doc: pdfjsLib.PDFDocumentProxy | null = null
  let page: pdfjsLib.PDFPageProxy | null = null

  try {
    if (signal?.aborted) return

    const buffer = await pdfBlob.arrayBuffer()

    if (signal?.aborted) return

    doc = await pdfjsLib.getDocument({
      data: buffer,
      useSystemFonts: true,
    }).promise

    if (signal?.aborted) return

    page = await doc.getPage(pageNumber)

    const viewport = page.getViewport({ scale })

    const canvas = document.createElement("canvas")
    const ctx = canvas.getContext("2d", { alpha: false })

    if (!ctx) throw new Error("Canvas context not available")

    canvas.width = viewport.width
    canvas.height = viewport.height

    const renderTask = page.render({
      canvasContext: ctx,
      viewport,
    })

    if (signal) {
      signal.addEventListener("abort", () => {
        try {
          renderTask.cancel()
        } catch {}
      })
    }

    await renderTask.promise

    if (signal?.aborted) return

    const dataUrl = canvas.toDataURL("image/png")

    // cleanup canvas memory
    canvas.width = 0
    canvas.height = 0

    return dataUrl
  } catch (err: any) {
    if (err?.name === "RenderingCancelledException") return
    console.error("[pdfBlobToImageDataUrl] error:", err)
    return
  } finally {
    try {
      page?.cleanup()
      await doc?.destroy()
    } catch {}
  }
}
